import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useHistory } from 'react-router-dom'
import { confirmPasswordReset } from 'firebase/auth'
import { firebaseAuth } from '../../../services/FirebaseConfig'
import Swal from 'sweetalert2'
import '../../../../i18n/config'
import { useTranslation } from 'react-i18next'

const recoverSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, 'Mínimo 6 caracteres')
    .max(50, 'Máximo 50 caracteres')
    .required('Senha é obrigatória'),
  confirmPassword: Yup.string()
    .required('Confirmação é obrigatória'),
})

export function RecoverPassword() {
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const { t } = useTranslation()

  const urlParams = new URLSearchParams(window.location.search)
  const oobCode = urlParams.get('oobCode') ?? ''

  const formik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: recoverSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      if (values.password !== values.confirmPassword) {
        setStatus(t('As senhas não são iguais'))
        return
      }

      if (!oobCode) {
        setStatus(t('Link inválido ou expirado.'))
        return
      }

      setLoading(true)
      try {
        await confirmPasswordReset(firebaseAuth, oobCode, values.password)
        await Swal.fire({
          text: t('Sua senha foi definida com sucesso. Você será redirecionado em 3 segundos...'),
          timer: 3000,
          icon: 'success',
          showConfirmButton: false,
        })
        history.push('/login')
      } catch (error: any) {
        setLoading(false)
        setSubmitting(false)
        const msg = error?.code === 'auth/expired-action-code'
          ? t('O link expirou. Solicite um novo.')
          : error?.code === 'auth/weak-password'
          ? t('A senha deve ter pelo menos 6 caracteres.')
          : t('Não foi possível alterar a senha.')
        setStatus(msg)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>{t('Senha')}</label>
        <input
          placeholder='Senha'
          type='password'
          autoComplete='off'
          {...formik.getFieldProps('password')}
          className={clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': formik.touched.password && formik.errors.password,
            'is-valid': formik.touched.password && !formik.errors.password,
          })}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.password}</span>
          </div>
        )}
      </div>

      <div className='fv-row mb-5 mt-10'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>{t('Confirmar Senha')}</label>
        <input
          placeholder='Confirmar Senha'
          type='password'
          autoComplete='off'
          {...formik.getFieldProps('confirmPassword')}
          className={clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword,
            'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
          })}
        />
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.confirmPassword}</span>
          </div>
        )}
      </div>

      <div className='text-center'>
        <button
          type='submit'
          className='btn btn-lg btn-primario w-100 mb-5'
          disabled={loading || !oobCode}
        >
          {!loading
            ? <span className='indicator-label'>DEFINIR SENHA</span>
            : <span className='indicator-progress' style={{ display: 'block' }}>
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                {t('Aguarde...')}
              </span>
          }
        </button>
      </div>
    </form>
  )
}
